import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import celebrate from '../utils/celebrate';
import PageArrival from '../components/PageArrival';
import { BookOpen as A1, Award as A2, Users as A3, FileText as A4, Star as A5, CheckCircle as A6 } from 'lucide-react';

import { useTenant } from '../context/TenantContext';
import { AppLayout, GlassCard, PageHeader, LoadingSpinner } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  BookOpen, Upload, Plus, FileText, Award, Target, TrendingUp,
  RefreshCw, ChevronDown, ChevronRight, Trash2, GraduationCap,
  BarChart3, AlertTriangle, CheckCircle, Star, Clock, X, Activity, ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { showFriendlyError } from '../utils/friendlyErrors';
import PatriciaPageGuide from '../components/PatriciaPageGuide';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GRADE_LEVELS = ['Middle School', '6th', '7th', '8th', '9th', '10th', '11th', '12th', 'College', 'Freshman', 'Sophomore', 'Junior', 'Senior'];
const DOC_TYPES = [
  { value: 'transcript', label: 'Transcript' },
  { value: 'report_card', label: 'Report Card' },
  { value: 'test_score', label: 'Test Score Report' },
  { value: 'award_letter', label: 'Award Letter' },
  { value: 'certificate', label: 'Certificate' },
  { value: 'accolade', label: 'Accolade' },
  { value: 'other', label: 'Other' },
];
const RECORD_TYPES = [
  { value: 'gpa', label: 'GPA', icon: GraduationCap },
  { value: 'test_score', label: 'Test Score', icon: FileText },
  { value: 'activity', label: 'Activity / Club', icon: Activity },
  { value: 'award', label: 'Award / Honor', icon: Award },
  { value: 'service', label: 'Community Service', icon: Star },
];

// Score ring component
function ScoreRing({ score, size = 120, label }) {
  const { branding } = useTenant();
  const radius = (size - 12) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  const color = branding?.is_white_label ? branding.primary_color :
    score >= 80 ? '#22c55e' : score >= 60 ? '#eab308' : score >= 40 ? '#f97316' : '#ef4444';

  return (
    <div className="flex flex-col items-center gap-1">
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#334155" strokeWidth="8" />
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={color} strokeWidth="8"
          strokeDasharray={circumference} strokeDashoffset={offset}
          strokeLinecap="round" style={{ transition: 'stroke-dashoffset 1s ease' }} />
      </svg>
      <div className="absolute flex flex-col items-center justify-center" style={{ width: size, height: size }}>
        <span className="text-3xl font-bold text-white">{score}</span>
        <span className="text-xs text-slate-300">/ 100</span>
      </div>
      {label && <span className="text-sm text-slate-300 mt-1">{label}</span>}
    </div>
  );
}

// Sub-score bar
function SubScoreBar({ label, score, icon: Icon }) {
  const color = score >= 80 ? 'bg-emerald-500' : score >= 60 ? 'bg-cyan-400' : score >= 40 ? 'bg-violet-500' : 'bg-red-500';
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-sm">
        <span className="text-slate-300 flex items-center gap-1.5">
          {Icon && <Icon className="w-3.5 h-3.5" />} {label}
        </span>
        <span className="text-white font-medium">{score}</span>
      </div>
      <div className="h-2 bg-white/[0.07] rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color} transition-all duration-700`} style={{ width: `${score}%` }} />
      </div>
    </div>
  );
}

// Upload Modal
function UploadModal({ onClose, onUpload }) {
  const [file, setFile] = useState(null);
  const [docType, setDocType] = useState('transcript');
  const [gradeLevel, setGradeLevel] = useState('');
  const [academicYear, setAcademicYear] = useState('');
  const [name, setName] = useState('');
  const [uploading, setUploading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error('Please select a file'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('File must be under 5MB'); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('doc_type', docType);
      formData.append('grade_level', gradeLevel);
      formData.append('academic_year', academicYear);
      formData.append('name', name || file.name);
      await onUpload(formData);
      onClose();
    } catch {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0a1729]/60 backdrop-blur-sm" data-testid="upload-modal">
      <div className="glass-card p-6 w-full max-w-md mx-4 border border-white/12">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-white">Upload Document</h3>
          <button onClick={onClose} className="text-slate-300 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm text-slate-300 mb-1 block">File (PDF, JPG, PNG)</label>
            <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setFile(e.target.files[0])}
              className="w-full text-sm text-slate-300 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-white/[0.07] file:text-white hover:file:bg-slate-600"
              data-testid="upload-file-input" />
          </div>
          <div>
            <label className="text-sm text-slate-300 mb-1 block">Document Name</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g., Junior Year Transcript" data-testid="upload-name-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm text-slate-300 mb-1 block">Document Type</label>
              <select value={docType} onChange={(e) => setDocType(e.target.value)}
                className="w-full bg-[#0b1b30] border border-white/12 rounded-lg px-3 py-2 text-white text-sm" data-testid="upload-doc-type">
                {DOC_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm text-slate-300 mb-1 block">Grade Level</label>
              <select value={gradeLevel} onChange={(e) => setGradeLevel(e.target.value)}
                className="w-full bg-[#0b1b30] border border-white/12 rounded-lg px-3 py-2 text-white text-sm" data-testid="upload-grade-level">
                <option value="">Select Grade</option>
                {GRADE_LEVELS.map(g => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-sm text-slate-300 mb-1 block">Academic Year</label>
            <Input value={academicYear} onChange={(e) => setAcademicYear(e.target.value)} placeholder="e.g., 2024-2025" data-testid="upload-academic-year" />
          </div>
          <Button type="submit" disabled={uploading || !file} className="w-full" data-testid="upload-submit-btn">
            {uploading ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Uploading & Analyzing...</> : <><Upload className="w-4 h-4 mr-2" /> Upload Document</>}
          </Button>
        </form>
      </div>
    </div>
  );
}

// Add Record Modal
function AddRecordModal({ onClose, onAdd }) {
  const [recordType, setRecordType] = useState('gpa');
  const [title, setTitle] = useState('');
  const [score, setScore] = useState('');
  const [maxScore, setMaxScore] = useState('');
  const [gradeLevel, setGradeLevel] = useState('');
  const [academicYear, setAcademicYear] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      await onAdd({
        record_type: recordType, title, description,
        score: score ? parseFloat(score) : null,
        max_score: maxScore ? parseFloat(maxScore) : null,
        grade_level: gradeLevel, academic_year: academicYear,
      });
      onClose();
    } catch {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0a1729]/60 backdrop-blur-sm" data-testid="add-record-modal">
      <div className="glass-card p-6 w-full max-w-md mx-4 border border-white/12">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-white">Add Academic Record</h3>
          <button onClick={onClose} className="text-slate-300 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm text-slate-300 mb-1 block">Record Type</label>
            <div className="grid grid-cols-3 gap-2">
              {RECORD_TYPES.map(rt => (
                <button key={rt.value} type="button" onClick={() => setRecordType(rt.value)}
                  className={`flex flex-col items-center gap-1 p-2 rounded-lg border text-xs transition-colors ${recordType === rt.value ? 'border-cyan-400 bg-cyan-400/[0.10] text-cyan-300' : 'border-white/12 text-slate-300 hover:border-slate-500'}`}
                  data-testid={`record-type-${rt.value}`}>
                  <rt.icon className="w-4 h-4" />
                  {rt.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-sm text-slate-300 mb-1 block">Title</label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={recordType === 'gpa' ? 'e.g., Fall Semester GPA' : recordType === 'test_score' ? 'e.g., SAT Score' : 'e.g., Chess Club President'} data-testid="record-title-input" />
          </div>
          {(recordType === 'gpa' || recordType === 'test_score' || recordType === 'service') && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm text-slate-300 mb-1 block">{recordType === 'service' ? 'Hours' : 'Score'}</label>
                <Input type="number" step="any" value={score} onChange={(e) => setScore(e.target.value)} placeholder={recordType === 'gpa' ? '3.8' : recordType === 'service' ? '50' : '1400'} data-testid="record-score-input" />
              </div>
              <div>
                <label className="text-sm text-slate-300 mb-1 block">{recordType === 'service' ? 'Goal Hours' : 'Max Score'}</label>
                <Input type="number" step="any" value={maxScore} onChange={(e) => setMaxScore(e.target.value)} placeholder={recordType === 'gpa' ? '4.0' : recordType === 'service' ? '100' : '1600'} data-testid="record-max-score-input" />
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm text-slate-300 mb-1 block">Grade Level</label>
              <select value={gradeLevel} onChange={(e) => setGradeLevel(e.target.value)}
                className="w-full bg-[#0b1b30] border border-white/12 rounded-lg px-3 py-2 text-white text-sm" data-testid="record-grade-level">
                <option value="">Select Grade</option>
                {GRADE_LEVELS.map(g => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm text-slate-300 mb-1 block">Academic Year</label>
              <Input value={academicYear} onChange={(e) => setAcademicYear(e.target.value)} placeholder="2024-2025" data-testid="record-academic-year" />
            </div>
          </div>
          <div>
            <label className="text-sm text-slate-300 mb-1 block">Description (optional)</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} placeholder="Additional details..."
              className="w-full bg-[#0b1b30] border border-white/12 rounded-lg px-3 py-2 text-white text-sm resize-none" data-testid="record-description" />
          </div>
          <Button type="submit" disabled={saving || !title} className="w-full" data-testid="record-submit-btn">
            {saving ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : <><Plus className="w-4 h-4 mr-2" /> Add Record</>}
          </Button>
        </form>
      </div>
    </div>
  );
}

// Timeline Node
function TimelineNode({ grade, data, isActive, onToggle, onDeleteRecord, onDeleteDoc, brandColor }) {
  const hasData = data.records.length > 0 || data.documents.length > 0;
  const rating = data.rating;
  const typeIcons = { gpa: GraduationCap, test_score: FileText, activity: Activity, award: Award, service: Star };

  return (
    <div className="relative pl-8 pb-6" data-testid={`timeline-node-${grade.replace(/\s/g, '-')}`}>
      {/* Vertical line */}
      <div className="absolute left-3 top-0 bottom-0 w-0.5 bg-white/[0.07]" />
      {/* Node dot */}
      <div className={`absolute left-1 top-1 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${hasData ? 'border-cyan-400 bg-cyan-400/[0.15]' : 'border-white/12 bg-[#0b1b30]'}`}
        style={hasData && brandColor ? { borderColor: brandColor, backgroundColor: `${brandColor}20` } : undefined}>
        {hasData && <div className="w-2 h-2 rounded-full bg-blue-500" style={brandColor ? { backgroundColor: brandColor } : undefined} />}
      </div>
      {/* Content */}
      <button onClick={onToggle} className="flex items-center gap-2 mb-2 group">
        <span className={`font-medium ${hasData ? 'text-white' : 'text-slate-300'}`}>{grade}</span>
        {hasData && (
          <>
            <Badge variant="secondary" className="text-xs">{data.records.length + data.documents.length} items</Badge>
            {rating && <Badge className="text-xs bg-cyan-400/[0.15] text-cyan-300">{rating.overall_score}/100</Badge>}
            {isActive ? <ChevronDown className="w-4 h-4 text-slate-300" /> : <ChevronRight className="w-4 h-4 text-slate-300" />}
          </>
        )}
      </button>
      {isActive && hasData && (
        <div className="space-y-2 ml-2 animate-in fade-in duration-200">
          {data.documents.map(doc => (
            <div key={doc.id} className="flex items-center justify-between bg-white/[0.04] rounded-lg px-3 py-2 group">
              <div className="flex items-center gap-2 min-w-0">
                <Upload className="w-4 h-4 text-cyan-400 shrink-0" />
                <span className="text-sm text-slate-200 truncate">{doc.name}</span>
                <Badge variant="outline" className="text-xs shrink-0">{doc.doc_type}</Badge>
              </div>
              <button onClick={() => onDeleteDoc(doc.id)} className="opacity-0 group-hover:opacity-100 text-slate-300 hover:text-red-400 transition-all" data-testid={`delete-doc-${doc.id}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
          {data.records.map(rec => {
            const RecIcon = typeIcons[rec.record_type] || FileText;
            return (
              <div key={rec.id} className="flex items-center justify-between bg-white/[0.04] rounded-lg px-3 py-2 group">
                <div className="flex items-center gap-2 min-w-0">
                  <RecIcon className="w-4 h-4 text-violet-300 shrink-0" />
                  <span className="text-sm text-slate-200 truncate">{rec.title}</span>
                  {rec.score != null && <span className="text-xs text-slate-300">{rec.score}{rec.max_score ? `/${rec.max_score}` : ''}</span>}
                </div>
                <button onClick={() => onDeleteRecord(rec.id)} className="opacity-0 group-hover:opacity-100 text-slate-300 hover:text-red-400 transition-all" data-testid={`delete-rec-${rec.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function AnalysisStatus({ rating, stats, onRefresh, loading }) {
  if (!rating) return null;
  const analyzedDocs = rating.evidence?.analyzed_documents ?? rating.document_count ?? 0;
  const stale = (stats.docs || 0) !== (rating.document_count || 0) || (stats.records || 0) !== (rating.record_count || 0);
  const ev = rating.evidence || {};
  const chips = [
    ev.best_gpa != null && `GPA ${ev.best_gpa}`,
    ...(ev.tests || []).map(t => `${t.test} ${t.score}${t.percentile ? ` (${t.percentile}th)` : ''}`),
    ev.ap_honors_count ? `${ev.ap_honors_count} advanced courses` : null,
    ev.award_count ? `${ev.award_count} awards` : null,
    ev.activity_count ? `${ev.activity_count} activities` : null,
  ].filter(Boolean);

  return (
    <div className="mb-4" data-testid="analysis-status">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-300">
        <span data-testid="analysis-timestamp">
          Last analyzed {new Date(rating.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
        </span>
        <span className="text-slate-300">•</span>
        <span data-testid="analysis-evidence-count">{analyzedDocs} document{analyzedDocs === 1 ? '' : 's'} + {rating.record_count || 0} record{rating.record_count === 1 ? '' : 's'} analyzed</span>
      </div>
      {chips.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2" data-testid="evidence-chips">
          {chips.map((c, i) => (
            <span key={i} className="px-2 py-0.5 rounded-full bg-cyan-400/[0.10] text-xs text-slate-200 border border-white/12">{c}</span>
          ))}
        </div>
      )}
      {stale && (
        <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-2 p-3 rounded-lg bg-violet-500/[0.10] border border-violet-400/30" data-testid="stale-analysis-banner">
          <AlertTriangle className="w-4 h-4 text-violet-300 shrink-0" />
          <p className="text-sm text-violet-100 flex-1">
            Your portfolio changed since this analysis. Refresh to include your newest documents and records in your score.
          </p>
          <Button size="sm" onClick={onRefresh} disabled={loading} data-testid="stale-refresh-btn">
            {loading ? <><RefreshCw className="w-4 h-4 mr-1 animate-spin" /> Analyzing...</> : <><RefreshCw className="w-4 h-4 mr-1" /> Refresh Analysis</>}
          </Button>
        </div>
      )}
    </div>
  );
}

export default function PortfolioPage() {
  const [arrived, setArrived] = useState(false);
  const { user, getAuthHeader } = useAuth();
  const { branding } = useTenant();
  const [loading, setLoading] = useState(true);
  const [timeline, setTimeline] = useState({});
  const [allGrades, setAllGrades] = useState([]);
  const [latestRating, setLatestRating] = useState(null);
  const [ratingHistory, setRatingHistory] = useState([]);
  const [showUpload, setShowUpload] = useState(false);
  const [showAddRecord, setShowAddRecord] = useState(false);
  const [expandedGrades, setExpandedGrades] = useState({});
  const [ratingLoading, setRatingLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState({ records: 0, docs: 0, ratings: 0 });
  const [milestones, setMilestones] = useState([]);
  const [newMilestones, setNewMilestones] = useState([]);
  const [admissionsProfile, setAdmissionsProfile] = useState(null);

  const brandColor = branding?.is_white_label ? branding.primary_color : null;

  const fetchAll = useCallback(async () => {
    try {
      const headers = getAuthHeader();
      const [timelineRes, ratingRes, historyRes, milestonesRes, admissionsRes] = await Promise.all([
        axios.get(`${API}/portfolio/timeline`, headers).catch(() => ({ data: { timeline: {}, all_grades: GRADE_LEVELS, total_records: 0, total_documents: 0, total_ratings: 0 } })),
        axios.get(`${API}/portfolio/latest-rating`, headers).catch(() => ({ data: { rating: null } })),
        axios.get(`${API}/portfolio/ratings`, headers).catch(() => ({ data: { ratings: [] } })),
        axios.get(`${API}/portfolio/milestones`, headers).catch(() => ({ data: { milestones: [] } })),
        axios.get(`${API}/portfolio/admissions-profile`, headers).catch(() => ({ data: null })),
      ]);

      setTimeline(timelineRes.data.timeline || {});
      setAllGrades(timelineRes.data.all_grades || GRADE_LEVELS);
      setStats({ records: timelineRes.data.total_records, docs: timelineRes.data.total_documents, ratings: timelineRes.data.total_ratings });
      setLatestRating(ratingRes.data.rating);
      setRatingHistory(historyRes.data.ratings || []);
      setMilestones(milestonesRes.data.milestones || []);
      if (admissionsRes.data) setAdmissionsProfile(admissionsRes.data);

      // Auto-expand grades with data
      const expanded = {};
      Object.keys(timelineRes.data.timeline || {}).forEach(g => { expanded[g] = true; });
      setExpandedGrades(expanded);
    } catch (err) {
      console.error('Failed to load portfolio:', err);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleUpload = async (formData) => {
    try {
      await axios.post(`${API}/portfolio/upload-document`, formData, {
        ...getAuthHeader(),
        headers: { ...getAuthHeader().headers, 'Content-Type': 'multipart/form-data' },
        maxContentLength: 5 * 1024 * 1024,
        timeout: 60000,
      });
      toast.success('Document uploaded and analyzed. Refresh your analysis to update your score.');
      fetchAll();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Upload failed. Please ensure the file is under 5MB.';
      toast.error(msg);
      throw err;
    }
  };

  const handleAddRecord = async (data) => {
    try {
      await axios.post(`${API}/portfolio/add-record`, data, getAuthHeader());
      toast.success('Record added successfully');
      fetchAll();
    } catch (err) {
      showFriendlyError(err, { fallback: 'Failed to add record' });
      throw err;
    }
  };

  const handleDeleteRecord = async (id) => {
    try {
      await axios.delete(`${API}/portfolio/record/${id}`, getAuthHeader());
      toast.success('Record deleted');
      fetchAll();
    } catch { toast.error('Failed to delete record'); }
  };

  const handleDeleteDoc = async (id) => {
    try {
      await axios.delete(`${API}/portfolio/document/${id}`, getAuthHeader());
      toast.success('Document deleted');
      fetchAll();
    } catch { toast.error('Failed to delete document'); }
  };

  const handleRateProfile = async () => {
    setRatingLoading(true);
    try {
      // Make sure every uploaded document has been parsed with the latest extractor
      for (let i = 0; i < 8; i++) {
        try {
          const prep = await axios.post(`${API}/portfolio/prepare-analysis`, {}, { ...getAuthHeader(), timeout: 55000 });
          if (!prep.data?.remaining) break;
        } catch { break; }
      }
      const res = await axios.post(`${API}/ai/rate-profile`, {}, { ...getAuthHeader(), timeout: 55000 });
      setLatestRating(res.data.rating);
      // Check for new milestones
      if (res.data.new_milestones?.length > 0) {
        setNewMilestones(res.data.new_milestones);
        res.data.new_milestones.forEach(ms => {
          celebrate(`Milestone achieved: ${ms.title}`);
        });
      } else {
        toast.success('Profile analysis complete');
      }
      fetchAll();
    } catch (err) {
      showFriendlyError(err, { fallback: 'Analysis failed' });
    } finally {
      setRatingLoading(false);
    }
  };

  const handleSnapshot = async () => {
    try {
      await axios.post(`${API}/portfolio/snapshot`, { grade_level: user?.grade_level || '', academic_year: '' }, getAuthHeader());
      toast.success('Grade snapshot saved');
      fetchAll();
    } catch (err) {
      showFriendlyError(err, { fallback: 'Snapshot failed' });
    }
  };

  if (loading) {
    return (
      <AppLayout>
      <div className="mb-4"><PatriciaPageGuide pageId="portfolio" /></div>
        <div className="flex items-center justify-center h-64"><LoadingSpinner size="lg" /></div>
      </AppLayout>
    );
  }

  const r = latestRating;
  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'timeline', label: 'Timeline', icon: Clock },
    { id: 'feedback', label: 'AI Feedback', icon: Target },
  ];

  if (!arrived) return (
    <AppLayout>
      <PageArrival
        testid="portfolio-arrival"
        eyebrow="Academic Portfolio"
        title="The record behind the application."
        subtitle="Courses, grades, activities, service, awards and roles, gathered while you remember them, so applications and references write themselves later."
        accent="#a78bfa"
        second="#34d399"
        ctaLabel="Build my portfolio"
        narration={"This is your Academic Portfolio. The details that win applications are the ones people forget, the hours you served, the role you held, the project you finished. Log them while they are fresh and applications become assembly rather than archaeology, and anyone writing you a reference has something real to work from."}
        facts={[
          { icon: A1, head: "Logged as you go", sub: "Not reconstructed from memory later" },
          { icon: A4, head: "Application-ready", sub: "Everything an application asks for" },
          { icon: A3, head: "Helps references", sub: "Real detail for whoever recommends you" }
        ]}
        modules={[
          { icon: A1, label: "Courses and grades", line: "Your academic record in one view", hue: "#a78bfa" },
          { icon: A5, label: "Activities and roles", line: "What you did, and what you led", hue: "#22d3ee" },
          { icon: A3, label: "Service hours", line: "Logged, totalled and verifiable", hue: "#34d399" },
          { icon: A2, label: "Awards", line: "Recognition worth naming", hue: "#fbbf24" },
          { icon: A4, label: "Projects", line: "Work you can actually show", hue: "#f472b6" },
          { icon: A6, label: "Export", line: "Straight into applications and résumés", hue: "#60a5fa" }
        ]}
        onStart={() => { setArrived(true); window.scrollTo({ top: 0 }); }}
      />
    </AppLayout>
  );

  // ── Completeness model (25 checkpoints) ────────────────────────────────
  const allRecords = Object.values(timeline).flatMap(g =>
    Object.values(g).flatMap(items => (Array.isArray(items) ? items : []))
  );
  const hasType = (t) => allRecords.some(r => r.record_type === t || r.doc_type === t);

  const gradeNum = parseInt(user?.grade || user?.grade_level || 0);
  const gradeName = gradeNum === 9 ? 'Freshman' : gradeNum === 10 ? 'Sophomore'
    : gradeNum === 11 ? 'Junior' : gradeNum === 12 ? 'Senior' : `Grade ${gradeNum}`;
  const gradYear = user?.graduation_year || (gradeNum ? 2024 + (12 - gradeNum) : null);

  const ITEMS = [
    // Academic Snapshot (5)
    { id:'grade',         label:'Grade Level',          sec:'academic',    priority:'low',    filled:!!gradeNum },
    { id:'grad_year',     label:'Graduation Year',       sec:'academic',    priority:'low',    filled:!!gradYear },
    { id:'gpa',           label:'Unweighted GPA',        sec:'academic',    priority:'high',   filled:!!user?.gpa },
    { id:'wgpa',          label:'Weighted GPA',          sec:'academic',    priority:'high',   filled:!!user?.weighted_gpa },
    { id:'class_rank',    label:'Class Rank',            sec:'academic',    priority:'medium', filled:!!user?.class_rank },
    // Coursework (4)
    { id:'completed',     label:'Completed Courses',     sec:'coursework',  priority:'high',   filled:hasType('course') || hasType('coursework') },
    { id:'current',       label:'Current Courses',       sec:'coursework',  priority:'high',   filled:hasType('current_course') },
    { id:'planned',       label:'Planned Courses',       sec:'coursework',  priority:'medium', filled:hasType('planned_course') },
    { id:'rigor',         label:'Course Rigor (AP/Honors)',sec:'coursework', priority:'medium', filled:hasType('ap') || hasType('honors') },
    // Testing (4)
    { id:'sat',           label:'SAT Score',             sec:'testing',     priority:'high',   filled:!!user?.sat_score },
    { id:'act',           label:'ACT Score',             sec:'testing',     priority:'high',   filled:!!user?.act_score },
    { id:'ap_exams',      label:'AP Exams',              sec:'testing',     priority:'medium', filled:hasType('ap_exam') },
    { id:'test_plan',     label:'Testing Plan',          sec:'testing',     priority:'medium', filled:hasType('test_plan') },
    // Activities (5)
    { id:'athletics',     label:'Athletics',             sec:'activities',  priority:'medium', filled:hasType('athletics') || hasType('sport') },
    { id:'volunteer',     label:'Volunteer Service',     sec:'activities',  priority:'high',   filled:hasType('volunteer') || hasType('service') },
    { id:'employment',    label:'Employment',            sec:'activities',  priority:'medium', filled:hasType('employment') || hasType('job') },
    { id:'clubs',         label:'Clubs & Organizations', sec:'activities',  priority:'medium', filled:hasType('club') || hasType('organization') },
    { id:'internship',    label:'Internships',           sec:'activities',  priority:'high',   filled:hasType('internship') },
    // Achievements (4)
    { id:'awards',        label:'Awards',                sec:'achievements',priority:'medium', filled:hasType('award') || hasType('honor') },
    { id:'credentials',   label:'Credentials',           sec:'achievements',priority:'high',   filled:hasType('credential') || hasType('certificate') },
    { id:'projects',      label:'Projects',              sec:'achievements',priority:'medium', filled:hasType('project') },
    { id:'competitions',  label:'Competitions',          sec:'achievements',priority:'low',    filled:hasType('competition') },
    // College App (3)
    { id:'major',         label:'Intended Major',        sec:'college',     priority:'high',   filled:!!(user?.career_aspirations || user?.postsecondary_intent) },
    { id:'strategy',      label:'Application Strategy',  sec:'college',     priority:'high',   filled:hasType('application_strategy') },
    { id:'pathway',       label:'Special Pathway',       sec:'college',     priority:'medium', filled:!!(user?.postsecondary_intent) },
  ];

  // ── Completeness model, prefer enriched backend data ──────────────────
  const useEnriched = !!admissionsProfile;
  const missingHigh = ITEMS.filter(i => !i.filled && i.priority === 'high').slice(0, 3);
  const missingMed  = ITEMS.filter(i => !i.filled && i.priority === 'medium').slice(0, 2);

  const filledCount = useEnriched ? admissionsProfile.filled_count : ITEMS.filter(i => i.filled).length;
  const TOTAL = useEnriched ? admissionsProfile.total : ITEMS.length;
  const completePct = useEnriched ? admissionsProfile.completeness_pct : Math.round((filledCount / TOTAL) * 100);
  const completeLabel = useEnriched ? admissionsProfile.completeness_label : (completePct >= 80 ? 'Strong Profile' : completePct >= 55 ? 'Good Progress' : completePct >= 30 ? 'Getting Started' : 'Just Beginning');
  const completeColor = useEnriched ? admissionsProfile.completeness_color : (completePct >= 80 ? '#34d399' : completePct >= 55 ? '#22d3ee' : completePct >= 30 ? '#fbbf24' : '#fb923c');

  const missingItems = useEnriched ? admissionsProfile.missing_items : [...missingHigh, ...missingMed].slice(0, 5);
  const SECTIONS = useEnriched ? admissionsProfile.sections : [
    {
      num: 1, title: 'Academic Snapshot', color: '#22d3ee',
      rows: [
        { label: 'Grade', value: gradeNum ? `Grade ${gradeNum}` : null },
        { label: 'Graduation Year', value: gradYear ? String(gradYear) : null },
        { label: 'Unweighted GPA', value: user?.gpa ? `${user.gpa} / 4.0` : null },
        { label: 'Weighted GPA', value: user?.weighted_gpa ? `${user.weighted_gpa}` : null },
        { label: 'Class Rank', value: user?.class_rank || null },
      ],
    },
    {
      num: 2, title: 'Coursework & Rigor', color: '#a78bfa',
      rows: [
        { label: 'Completed', value: allRecords.filter(r => r.record_type === 'course').length || null, suffix: ' courses' },
        { label: 'Current Courses', value: allRecords.filter(r => r.record_type === 'current_course').length || null, suffix: ' enrolled' },
        { label: 'Planned', value: allRecords.filter(r => r.record_type === 'planned_course').length || null, suffix: ' courses' },
        { label: 'Course Rigor', value: allRecords.filter(r => ['ap','honors','dual_enrollment','ib'].includes(r.record_type)).length ? 'Logged' : null },
      ],
    },
    {
      num: 3, title: 'Testing & Assessments', color: '#fbbf24',
      rows: [
        { label: 'SAT', value: user?.sat_score ? `${user.sat_score} / 1600` : null },
        { label: 'ACT', value: user?.act_score ? `${user.act_score} / 36` : null },
        { label: 'AP Exams', value: allRecords.filter(r => r.record_type === 'ap_exam').length || null, suffix: ' logged' },
        { label: 'Testing Plan', value: hasType('test_plan') ? 'On file' : null },
      ],
    },
    {
      num: 4, title: 'Activities & Experiences', color: '#34d399',
      rows: [
        { label: 'Athletics', value: hasType('athletics') || hasType('sport') ? 'Logged' : null },
        { label: 'Volunteer Service', value: allRecords.filter(r => r.record_type === 'volunteer').reduce((a, r) => a + (r.hours || 0), 0) || null, suffix: ' hours' },
        { label: 'Employment', value: hasType('employment') ? 'Logged' : null },
        { label: 'Clubs & Orgs', value: allRecords.filter(r => r.record_type === 'club').length || null, suffix: ' listed' },
        { label: 'Internships', value: hasType('internship') ? 'Logged' : null },
      ],
    },
    {
      num: 5, title: 'Achievements & Evidence', color: '#f472b6',
      rows: [
        { label: 'Awards', value: allRecords.filter(r => r.record_type === 'award').length || null, suffix: ' added' },
        { label: 'Credentials', value: allRecords.filter(r => r.record_type === 'credential').length || null, suffix: ' on file' },
        { label: 'Projects', value: allRecords.filter(r => r.record_type === 'project').length || null, suffix: ' logged' },
        { label: 'Competitions', value: allRecords.filter(r => r.record_type === 'competition').length || null, suffix: ' listed' },
      ],
    },
    {
      num: 6, title: 'College Application Profile', color: '#60a5fa',
      rows: [
        { label: 'Intended Major', value: user?.career_aspirations || null },
        { label: 'Pathway', value: user?.postsecondary_intent ? user.postsecondary_intent.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : null },
        { label: 'Application Strategy', value: hasType('application_strategy') ? 'On file' : null },
        { label: 'Special Pathway', value: user?.postsecondary_intent || null },
      ],
    },
  ];

  const GUIDANCE = useEnriched ? [
    { label: 'Strongest area', value: admissionsProfile.guidance.strongest_area, sub: admissionsProfile.guidance.strongest_sub, color: '#34d399' },
    { label: 'Largest gap', value: admissionsProfile.guidance.largest_gap, sub: admissionsProfile.guidance.largest_gap_sub, color: '#fb923c' },
    { label: 'Recommended next step', value: admissionsProfile.guidance.next_step, sub: admissionsProfile.guidance.next_step_sub, color: '#22d3ee' },
    { label: 'Profile strength', value: completeLabel, sub: `${filledCount} of ${TOTAL} key items on file`, color: completeColor },
  ] : [
    {
      label: 'Strongest area',
      value: user?.sat_score ? `SAT ${user.sat_score}` : user?.gpa ? `GPA ${user.gpa}` : 'Keep filling in data',
      sub: 'Colleges will notice this first',
      color: '#34d399',
    },
    {
      label: 'Largest gap',
      value: missingItems[0]?.label || 'Profile looks good',
      sub: 'Fill this to unlock stronger matches',
      color: '#fb923c',
    },
    {
      label: 'Recommended next step',
      value: missingItems[0] ? `Add ${missingItems[0].label}` : 'Upload a transcript',
      sub: 'Highest-impact item you can add today',
      color: '#22d3ee',
    },
    {
      label: 'Profile strength',
      value: completeLabel,
      sub: `${filledCount} of ${TOTAL} key items on file`,
      color: completeColor,
    },
  ];

  return (
    <AppLayout>
      <div className="space-y-7 pb-10" data-testid="portfolio-dashboard">

        {/* ── Header ──────────────────────────────────────────────────────── */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-[11.5px] uppercase tracking-[0.22em] text-violet-300/80 mb-2">
              Academic Portfolio
            </p>
            <h1 className="text-4xl font-bold text-white">
              Build the profile colleges will read.
            </h1>
            <p className="text-sm text-slate-300 mt-2">
              {user?.full_name || 'Your'} academic record, keep it in sync as you go.
            </p>
          </div>
          <div className="flex gap-2 shrink-0">
            <button onClick={() => setShowAddRecord(true)}
              className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.04]
                px-4 py-2.5 text-sm font-medium text-slate-200 hover:bg-white/[0.08] transition-colors"
              data-testid="add-record-btn">
              <Plus className="h-4 w-4" /> Add Record
            </button>
            <button onClick={() => setShowUpload(true)}
              className="flex items-center gap-2 rounded-xl border border-violet-400/30
                bg-violet-500/[0.1] px-4 py-2.5 text-sm font-medium text-violet-200
                hover:bg-violet-500/20 transition-colors"
              data-testid="upload-doc-btn">
              <Upload className="h-4 w-4" /> Upload Document
            </button>
          </div>
        </div>

        {/* ── Completeness + Needs row ────────────────────────────────────── */}
        <div className="grid gap-5 lg:grid-cols-2">

          {/* Completeness ring */}
          <div className="rounded-2xl border border-cyan-400/20 bg-[#0b1b30] p-6 flex items-center gap-8
            shadow-[0_0_30px_rgba(34,211,238,0.06)]"
            data-testid="completeness-card">
            {/* SVG ring */}
            <div className="relative shrink-0">
              <svg width="120" height="120" viewBox="0 0 120 120">
                <circle cx="60" cy="60" r="50" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="10" />
                <circle cx="60" cy="60" r="50" fill="none" stroke={completeColor} strokeWidth="10"
                  strokeLinecap="round" strokeDasharray={`${2 * Math.PI * 50}`}
                  strokeDashoffset={`${2 * Math.PI * 50 * (1 - completePct / 100)}`}
                  transform="rotate(-90 60 60)" style={{ transition: 'stroke-dashoffset 1s ease' }} />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <p className="text-2xl font-black text-white tabular-nums">{completePct}%</p>
              </div>
            </div>
            <div>
              <p className="text-xl font-bold text-white">{filledCount} of {TOTAL} key items on file</p>
              <span className="mt-1.5 inline-block px-3 py-0.5 rounded-full text-xs font-semibold"
                style={{ background: `${completeColor}20`, color: completeColor, border: `1px solid ${completeColor}40` }}>
                {completeLabel}
              </span>
              <p className="mt-3 text-[12px] text-slate-300 leading-relaxed max-w-xs">
                A complete profile improves your college match accuracy and helps counselors write stronger references.
              </p>
            </div>
          </div>

          {/* What FuturePath still needs */}
          <div className="rounded-2xl border border-amber-400/20 bg-amber-400/[0.03] p-6"
            data-testid="needs-card">
            <p className="text-[11.5px] uppercase tracking-[0.18em] text-amber-300/80 mb-4">
              What FuturePath still needs
            </p>
            <div className="space-y-2.5">
              {missingItems.length === 0 ? (
                <p className="text-sm text-emerald-300">Your profile is complete!</p>
              ) : missingItems.map(item => (
                <div key={item.id} className="flex items-center gap-3">
                  <div className="h-4 w-4 rounded border border-white/20 bg-white/[0.04] shrink-0" />
                  <p className="flex-1 text-[12.5px] text-slate-200">{item.label}</p>
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                    item.priority === 'high' ? 'bg-rose-500/15 text-rose-300' :
                    item.priority === 'medium' ? 'bg-amber-500/15 text-amber-300' :
                    'bg-slate-500/15 text-slate-300'}`}>
                    {item.priority.toUpperCase()}
                  </span>
                </div>
              ))}
            </div>
            <button onClick={() => setShowAddRecord(true)}
              className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl border
                border-amber-400/25 bg-amber-400/[0.07] py-2.5 text-sm font-medium text-amber-200
                hover:bg-amber-400/[0.12] transition-colors">
              Complete missing information <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* ── 6 Section cards ────────────────────────────────────────────── */}
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {SECTIONS.map(sec => (
            <div key={sec.num}
              className="rounded-2xl border bg-[#0b1b30] p-5 space-y-3 transition-all hover:shadow-[0_0_24px_rgba(34,211,238,0.07)]"
              style={{ borderColor: `${sec.color}22` }}
              data-testid={`section-card-${sec.num}`}>
              <div className="flex items-center gap-2.5">
                <div className="h-5 w-5 rounded flex items-center justify-center text-[10px] font-bold shrink-0"
                  style={{ background: `${sec.color}22`, color: sec.color, border: `1px solid ${sec.color}40` }}>
                  {sec.num}
                </div>
                <p className="text-[12px] font-semibold text-white">{sec.title}</p>
              </div>
              <div className="space-y-2.5">
                {sec.rows.map(row => (
                  <div key={row.label} className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-[11.5px] text-slate-300">{row.label}</p>
                      {row.source && (
                        <p className="text-[9.5px] text-slate-300 mt-0.5">from {row.source}</p>
                      )}
                    </div>
                    {(row.value != null && row.value !== 0 && row.value !== '') ? (
                      <p className="text-[11.5px] font-medium text-white text-right shrink-0">
                        {row.value}{row.suffix || ''}
                      </p>
                    ) : (
                      <span className="text-[11.5px] text-slate-300 italic shrink-0">Not added</span>
                    )}
                  </div>
                ))}
              </div>
              <button onClick={() => setShowAddRecord(true)}
                className="w-full text-center text-[11.5px] text-slate-300 hover:text-slate-300 transition-colors pt-1">
                + Add to this section
              </button>
            </div>
          ))}
        </div>

        {/* ── FuturePath Guidance ─────────────────────────────────────────── */}
        <div>
          <p className="text-[11.5px] uppercase tracking-[0.18em] text-slate-300 mb-4">
            FuturePath Guidance
          </p>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {GUIDANCE.map(g => (
              <div key={g.label} className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
                <p className="text-[10px] uppercase tracking-wider text-slate-300 mb-2">{g.label}</p>
                <p className="text-[13px] font-semibold" style={{ color: g.color }}>{g.value}</p>
                <p className="text-xs text-slate-300 mt-1">{g.sub}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ── Upload progress / AI Rating (if available) ─────────────────── */}
        {latestRating && (
          <div className="rounded-2xl border border-violet-400/20 bg-violet-400/[0.03] p-6">
            <p className="text-[11.5px] uppercase tracking-[0.18em] text-violet-300/80 mb-4">
              AI Profile Analysis · Overall Score
            </p>
            <div className="flex items-center gap-6">
              <div className="text-5xl font-black tabular-nums" style={{ color: '#a78bfa' }}>
                {latestRating.overall_score}
              </div>
              <div>
                <p className="text-sm font-semibold text-white">{latestRating.label || 'Profile Rated'}</p>
                <p className="text-xs text-slate-300 mt-1">{latestRating.summary || 'Based on uploaded documents and records'}</p>
              </div>
            </div>
          </div>
        )}

        {!latestRating && stats.records === 0 && stats.docs === 0 && (
          <div className="rounded-2xl border border-white/[0.06] bg-white/[0.01] p-6 text-center">
            <p className="text-sm text-slate-300">
              Add records or upload a transcript to unlock your AI Profile Analysis.
            </p>
            <button onClick={handleRateProfile} disabled={ratingLoading}
              className="mt-3 inline-flex items-center gap-2 rounded-xl border border-violet-400/30
                bg-violet-500/[0.1] px-5 py-2.5 text-sm font-medium text-violet-200
                hover:bg-violet-500/20 transition-colors disabled:opacity-50"
              data-testid="rate-profile-btn">
              {ratingLoading ? <><RefreshCw className="h-4 w-4 animate-spin" /> Analyzing...</> : 'Run AI Profile Analysis'}
            </button>
          </div>
        )}
      </div>

      {showUpload && <UploadModal onClose={() => setShowUpload(false)} onUpload={handleUpload} />}
      {showAddRecord && <AddRecordModal onClose={() => setShowAddRecord(false)} onAdd={handleAddRecord} />}
      {newMilestones.length > 0 && (
        <div className="fixed bottom-6 right-6 z-50 space-y-2">
          {newMilestones.map((ms, i) => (
            <div key={i} className="rounded-xl border border-emerald-400/30 bg-[#0b1b30] px-5 py-3 shadow-xl">
              <p className="text-sm font-semibold text-emerald-300">Milestone: {ms.title}</p>
              <p className="text-xs text-slate-300 mt-0.5">{ms.description}</p>
            </div>
          ))}
        </div>
      )}
    </AppLayout>
  );
}
